/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Button, Col, DatePicker, Input, Row, Select, Typography, Form, InputNumber } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import Down from '@/assets/svgs/down.svg?react';
import { useState } from "react";
import {
  watchTypePhysicalActivity,
  watchAgePhysicalActivity,
  watchIntensityPhysicalActivity,
  watchSuitableObjectPhysicalActivity,
  getListSuitableObjectPhysicalActivity,
  resetListSuitableObjectPhysicalActivity
} from '@/redux/slices/regulate-level-physical-activity.slice';
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

type PhysicalActivityByIntensitySearchProps = {
  onSearch: (value: FilterPhysicalActivityByIntensity) => void;
}

type AgeSelectOption = {
  value: number;
  label: string;
  healthStatusBMIType: string;
};

export const ListPhysicalActivitiesFilter = ({
  onSearch
}: PhysicalActivityByIntensitySearchProps) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [isExpanded, setIsExpanded] = useState(true);
  const listTypePhysicalActivity = useAppSelector(watchTypePhysicalActivity);
  const listIntensityPhysicalActivity = useAppSelector(watchIntensityPhysicalActivity);
  const listSuitableObjectPhysicalActivity = useAppSelector(watchSuitableObjectPhysicalActivity);
  const listAgePhysicalActivity = useAppSelector(watchAgePhysicalActivity);

  const startDate = Form.useWatch('START_UPDATE_DATE', form);
  const endDate = Form.useWatch('END_UPDATE_DATE', form);

  const listTypePhysicalActivityOptions = listTypePhysicalActivity.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const listIntensityPhysicalActivityOptions = listIntensityPhysicalActivity.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const listSuitableObjectActivityOptions = listSuitableObjectPhysicalActivity.map((item) => ({
    value: item.id,
    label: item.description
  }));

  const listAgeActivityOptions = listAgePhysicalActivity.map((item) => ({
    value: item.ID,
    label: item.DESCRIPTION,
    healthStatusBMIType: item.HEALTH_STATUS_BMI_TYPE,
  }));

  const handle = () => {
    const values = form.getFieldsValue();
    onSearch(values);
  }

  const chooseAge = (option: AgeSelectOption) => {
    if (option) {
      const type = option.healthStatusBMIType;
      dispatch(getListSuitableObjectPhysicalActivity({ type: type, option: 'PHYSICAL_ACTIVITY' }));
    }
  };

  const handleReset = async () => {
    await dispatch(getListSuitableObjectPhysicalActivity({ type: '', option: 'PHYSICAL_ACTIVITY' }));
    form.resetFields();
    const values = form.getFieldsValue();
    onSearch(values);
  };

  return (
    <div>
      <div css={filterHeader} onClick={() => setIsExpanded(!isExpanded)}>
        <Typography.Title level={5} css={filterTitle}>
          BỘ LỌC <Down />
        </Typography.Title>
      </div>

      {isExpanded && (
        <>
          <Form
            form={form}
            css={formStyles}
          >
            <Row gutter={16} css={rowStyle}>
              <Col span={8} css={filterItem}>
                <Form.Item
                  label='Tên hoạt động thể lực'
                  name='NAME'
                  style={{ width: "100%" }}
                  css={customLabel}
                  labelCol={{ span: 8 }}
                  colon={false}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8} css={filterItem}>
                <Form.Item
                  label='Loại hình hoạt động thể lực'
                  name='PHYSICAL_ACTIVITY_TYPE_ID'
                  style={{ width: "100%" }}
                  css={customLabel}
                  labelCol={{ span: 8 }}
                  colon={false}
                >
                  <Select
                    allowClear
                    placeholder="Chọn loại hình hoạt động thể lực"
                    options={listTypePhysicalActivityOptions} />
                </Form.Item>
              </Col>
              <Col span={8} css={filterItem}>
                <Form.Item
                  label='Cường độ hoạt động thể lực'
                  name='PHYSICAL_ACTIVITY_LEVEL'
                  style={{ width: "100%" }}
                  css={customLabel}
                  labelCol={{ span: 8 }}
                  colon={false}
                >
                  <Select
                    allowClear
                    placeholder="Chọn loại cường độ hoạt động thể lực"
                    options={listIntensityPhysicalActivityOptions} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} css={rowFormStyle}>
              <Col span={8} css={filterItem}>
                <Form.Item
                  label="Hệ số MET (ML02/kg/phút)"
                  style={{ width: "100%" }}
                  css={customLabel}
                  labelCol={{ span: 8 }}
                  colon={false}
                >
                  <Row gutter={8} align="middle">
                    <Col span={11}>
                      <Form.Item
                        name='MET_FACTOR_MIN'
                        style={{ width: "100%", marginBottom: 0 }}
                        dependencies={['MET_FACTOR_MAX']}
                        rules={[
                          {
                            validator: (_, value) => {
                              const max = form.getFieldValue("MET_FACTOR_MAX");
                              if (value === undefined || value === null || max === undefined || max === null) {
                                return Promise.resolve();
                              }
                              if (value > max) {
                                return Promise.reject("Giá trị Từ nhỏ hơn hoặc bằng Đến");
                              }
                              return Promise.resolve();
                            },
                          }
                        ]}
                      >
                        <InputNumber
                          type="number"
                          placeholder="Từ"
                          style={{ width: "100%" }}
                          min={0}
                          step={0.1}
                          stringMode
                          decimalSeparator="."
                        />

                      </Form.Item>
                    </Col>
                    <Col span={2} style={{ textAlign: "center" }}>
                      -
                    </Col>
                    <Col span={11}>
                      <Form.Item
                        name='MET_FACTOR_MAX'
                        style={{ width: "100%", marginBottom: 0 }}
                        dependencies={['MET_FACTOR_MIN']}
                        rules={[
                          {
                            validator: (_, value) => {
                              const min = form.getFieldValue("MET_FACTOR_MIN");
                              if (value === undefined || value === null || min === undefined || min === null) {
                                return Promise.resolve();
                              }
                              if (value < min) {
                                return Promise.reject("Giá trị Đến phải lớn hơn hoặc bằng Từ");
                              }
                              return Promise.resolve();
                            },
                          }
                        ]}
                      >
                        <InputNumber
                          type="number"
                          placeholder="Đến"
                          style={{ width: "100%" }}
                          min={0}
                          step={0.1}
                          stringMode
                          decimalSeparator="."
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
              <Col span={8} css={filterItem}>
                <Form.Item
                  label='Độ tuổi'
                  name='AGE_RANGE_ID'
                  style={{ width: "100%" }}
                  css={customLabel}
                  labelCol={{ span: 8 }}
                  colon={false}
                >
                  <Select
                    allowClear
                    placeholder="Chọn độ tuổi"
                    options={listAgeActivityOptions}
                    onSelect={(_value, option) => chooseAge(option)}
                    onClear={() => {
                      dispatch(resetListSuitableObjectPhysicalActivity());
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={8} css={filterItem}>
                <Form.Item
                  label='Đối tượng phù hợp'
                  name='HEALTH_STATUS_IDS'
                  style={{ width: "100%" }}
                  css={customLabel}
                  labelCol={{ span: 8 }}
                  colon={false}
                >
                  <Select
                    mode='multiple'
                    placeholder="Chọn đối tượng phù hợp"
                    options={listSuitableObjectActivityOptions}
                    disabled={!(listSuitableObjectActivityOptions?.length)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} css={timeStyle}>
              <Col span={8} css={filterItem}>
                <Form.Item
                  label="Thời gian cập nhật"
                  style={{ width: "100%" }}
                  css={customLabel}
                  labelCol={{ span: 8 }}
                  colon={false}
                >
                  <Row gutter={8} style={{ width: "100%", alignItems: "center", justifyContent: "center" }}>
                    <Col span={11}>
                      <Form.Item
                        name='START_UPDATE_DATE'
                        style={{ width: "100%" }}
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          style={{ width: "100%" }}
                          disabledDate={(current) =>
                            endDate ? current && current > endDate.endOf('day') : false
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={2} style={{ textAlign: "center" }}> - </Col>
                    <Col span={11}>
                      <Form.Item
                        name='END_UPDATE_DATE'
                        style={{ width: "100%" }}
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          style={{ width: "100%" }}
                          disabledDate={(current) =>
                            startDate ? current && current < startDate.startOf('day') : false
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
            </Row>
            <Row justify="center">
              <Button
                icon={<ReloadOutlined />}
                css={resetButton}
                onClick={handleReset}
              >
                Đặt lại
              </Button>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                css={searchButton}
                onClick={handle}
              >
                Tìm kiếm
              </Button>
            </Row>
          </Form>
        </>
      )}
    </div>
  );
};

const filterHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const timeStyle = css`
  margin-top: 1rem;  
  .ant-form-item {
    margin-bottom: unset !important;
  }
  .ant-col .ant-col-2 {
    display: flex;
    justify-content: center;
    align-items: center;
  }
   .ant-row {
    margin-left: unset !important;
    margin-right: unset !important;
  }
  .ant-col .ant-col-11 {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }  
  
  .ant-form-item-row {
    align-items: center !important;
  }
 
`

const filterTitle = css`
  font-size: 14px;
  font-weight: bold;
  color: black;
  margin-bottom: 10px;
`;

const filterItem = css`
  display: flex;
  justify-content: start !important;
  gap: 0.5rem;

  label {
    height: 100% !important;
  }

  .ant-form-item-label {
    width: fit-content;
    max-width: 33.33%;
    height: 100% !important;
    text-align: start !important;
    white-space: normal;
    font-size: 14px;
    font-weight: 500;
    word-wrap: break-word;
    word-break: break-word;
  }

  input,
  .ant-select,
  .ant-picker {
    flex: 1;
    min-width: 6rem;
  }
`;

const customLabel = css`
  .ant-form-item-label > label {
    font-weight: normal !important;
  }

  width: 100%;
  margin-bottom: 0;
`;


const resetButton = css`
  margin-right: 10px;
`;

const searchButton = css`
  background: #007bff;
  color: white;
  &:hover {
    background: #0056b3;
  }
`;

const formStyles = css`
  padding: 1rem;
  gap: 1rem;
  .ant-row {
    align-items: center !important;
  }
`;

const rowStyle = css`
  .ant-form-item-row {
    align-items: center;
  }
`

const rowFormStyle = css`
  margin-top: 1rem !important;
  .ant-form-item-row {
    align-items: center;
  }
  .ant-form-item {
    margin-bottom: unset !important;
  }
  .ant-col .ant-col-2 {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .ant-col .ant-col-11 {
    padding-left: unset !important;
    padding-right: unset !important;
  }  
   .ant-row {
    margin-left: unset !important;
    margin-right: unset !important;
  }
`